import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subscription,
  timer,
} from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

const POLL_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_TRANSIENT_FAILURES = 3;
const TRACKED_JOBS_STORAGE_KEY = 'trackedJobs';
const GONE_MARKER = 'gone';
const JOB_GONE_MESSAGE =
  'This job is no longer available. Please upload the file again.';
const JOB_UNREACHABLE_MESSAGE =
  'Unable to check status after several attempts. Your import may still be running. Reload the page to resume.';

/**
 * `queued`/`running`/`success`/`error` come from the backend. `gone` and `unreachable` are
 * client-side terminal statuses. They are different on purpose.
 * `gone`: the job expired, or the cache was flushed. The work is lost.
 * `unreachable`: only the polling stopped. The job probably still runs.
 */
export type TrackedJobStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'error'
  | 'gone'
  | 'unreachable';

const TERMINAL_STATUSES: ReadonlyArray<TrackedJobStatus> = [
  'success',
  'error',
  'gone',
  'unreachable',
];

/**
 * A job that the client keeps watch on. There is no `progress` field. The backend does not
 * write progress during a run, so a percentage would show 0 for the full job.
 */
export interface TrackedJob {
  readonly id: string;
  readonly kind: string;
  readonly status: TrackedJobStatus;
  /**
   * Display string set by the producer (the uploaded filename for `catalog_import`). Jobs
   * made before this field live up to 7 days and have no `label`. Always read it optionally.
   */
  readonly label?: string;
  /**
   * Opaque context set by the client for the kind -> route builder
   * (job-tracker-indicator.component.ts). For `catalog_import` it holds the `catalog_type` of
   * the upload. The tracker does not read it: the tracker is reusable, not import-specific.
   * Jobs tracked before this field have none.
   */
  readonly context?: string;
  readonly createdAt?: string;
  readonly result?: unknown;
  readonly error?: string;
  /**
   * Set when the user clicks this job in the header panel. This is not `dismiss()`. A seen job
   * stays in the panel, but the badge stops counting it. A dismiss on click would cause the
   * reload-loses-context bug again. A job that was never seen has no value, not `false`. Jobs
   * tracked before this field have none, so read it optionally.
   */
  readonly seen?: boolean;
  /**
   * Payload set by the producer. A later action on this job reads it to rebuild the inputs.
   * For example, `catalog_import` keeps `{s3_key, catalog_type, dry_run}` (see the `create_job`
   * docstring in `api/utils/job.py`). The tracker does not read it, for the same reason as
   * `context`. It arrives with the first poll, like `label` and `createdAt`. A job that is still
   * `queued`, or older than this field, has none. Read it optionally.
   */
  readonly meta?: Record<string, unknown> | null;
}

/** What localStorage holds: the ids only. The server owns job state (see readRegistry). */
interface StoredJob {
  id: string;
  kind: string;
  label?: string;
  context?: string;
  seen?: boolean;
}

interface JobResponse {
  status: 'queued' | 'running' | 'success' | 'error';
  label?: string;
  created_at?: string;
  result?: unknown;
  error?: string;
  meta?: Record<string, unknown> | null;
}

/**
 * A poll failure is not an HttpErrorResponse. The global ErrorInterceptor
 * (shared/helpers/error.interceptor.ts:40-45) rewrites each HTTP error before a subscriber
 * runs. It supplies the response body `{data, meta, error}` when `body.data` holds values, or
 * a bare string. In both cases `err.status` is lost. Thus the backend puts the job-is-gone
 * marker in `data` (app-service views/listing/jobs.py).
 */
type InterceptedError = { data?: { status?: string }; error?: string } | string;

@Injectable({ providedIn: 'root' })
export class JobTrackerService {
  private readonly jobs = new BehaviorSubject<TrackedJob[]>([]);
  readonly jobs$ = this.jobs.asObservable();

  /** Consecutive failed polls per job id. Not persisted -- a page reload is itself a retry. */
  private readonly transientFailures = new Map<string, number>();
  private pollSubscription: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
  ) {
    this.updateJobs(
      this.readRegistry().map(stored => this.asPendingJob(stored)),
    );
  }

  track(jobId: string, kind: string, label?: string, context?: string): void {
    if (this.jobs.value.some(job => job.id === jobId)) {
      return;
    }
    this.updateRegistry([
      ...this.jobs.value,
      this.asPendingJob({ id: jobId, kind, label, context }),
    ]);
  }

  /** The only way a job leaves the tracker. Terminal jobs stay put until this is called. */
  dismiss(jobId: string): void {
    this.transientFailures.delete(jobId);
    this.updateRegistry(this.jobs.value.filter(job => job.id !== jobId));
  }

  /**
   * Marks a job seen but keeps it. This persists like `dismiss()`, but it does not remove the
   * job. The badge (job-tracker-indicator.component.ts) stops counting a seen job. The panel
   * shows the job until `dismiss()` runs.
   */
  markSeen(jobId: string): void {
    const job = this.jobs.value.find(j => j.id === jobId);
    if (!job || job.seen) {
      return;
    }
    this.updateRegistry(
      this.jobs.value.map(j => (j.id === jobId ? { ...j, seen: true } : j)),
    );
  }

  private updateJobs(jobs: TrackedJob[]): void {
    this.jobs.next(jobs);
    this.syncTimer();
  }

  /** The registry changed, not just job state, so localStorage has to follow. */
  private updateRegistry(jobs: TrackedJob[]): void {
    this.writeRegistry(jobs);
    this.updateJobs(jobs);
  }

  private syncTimer(): void {
    if (this.activeJobs().length) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  private activeJobs(): TrackedJob[] {
    return this.jobs.value.filter(
      job => !TERMINAL_STATUSES.includes(job.status),
    );
  }

  /** One timer for every tracked job: N jobs cost N requests per tick, not N timers. */
  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    // Use exhaustMap, not switchMap. With switchMap, each next tick aborts a slow response.
    // Then no result applies, no failure counts, and the timer never stops. exhaustMap drops
    // the ticks that occur while a request is in flight.
    this.pollSubscription = timer(0, POLL_INTERVAL_MS)
      .pipe(exhaustMap(() => this.pollActiveJobs()))
      // The stream cannot error today (catchError sits under every request), but if it ever did,
      // a non-null dead subscription would make startPolling() a no-op for the rest of the session.
      .subscribe({ error: () => this.stopPolling() });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  private pollActiveJobs(): Observable<TrackedJob[]> {
    return forkJoin(this.activeJobs().map(job => this.fetchJob(job))).pipe(
      tap(polled => this.applyPolled(polled)),
    );
  }

  private fetchJob(job: TrackedJob): Observable<TrackedJob> {
    return this.http
      .get<{
        data: JobResponse;
      }>(`${environment.api_url_v1}/services/listing/jobs/${job.id}`)
      .pipe(
        // A healthy poll clears the give-up counter: a counter that only ever climbs would
        // eventually kill a long, perfectly healthy job.
        tap(() => this.transientFailures.delete(job.id)),
        map(response => this.merged(job, response.data)),
        catchError((err: InterceptedError) =>
          of(this.afterFailedPoll(job, err)),
        ),
      );
  }

  private applyPolled(polled: TrackedJob[]): void {
    const byId = new Map(polled.map(job => [job.id, job]));
    // Map over the CURRENT list, so a job dismissed while its request was in flight stays gone.
    this.updateJobs(this.jobs.value.map(job => byId.get(job.id) ?? job));
  }

  private merged(tracked: TrackedJob, job: JobResponse): TrackedJob {
    return {
      ...tracked,
      status: job.status,
      label: job.label ?? tracked.label,
      createdAt: job.created_at ?? tracked.createdAt,
      result: job.result ?? undefined,
      error: job.error ?? undefined,
      meta: job.meta ?? tracked.meta,
    };
  }

  /**
   * Only `gone` is terminal by absence. Each other failure (network loss, 500, gateway
   * timeout) is transient, because the job probably still runs on the server. Polling
   * continues and the tracked id stays.
   */
  private afterFailedPoll(
    tracked: TrackedJob,
    err: InterceptedError,
  ): TrackedJob {
    if (this.isGone(err)) {
      this.transientFailures.delete(tracked.id);
      return {
        ...tracked,
        status: 'gone',
        error: this.messageOf(err) ?? JOB_GONE_MESSAGE,
      };
    }

    const failures = (this.transientFailures.get(tracked.id) ?? 0) + 1;
    this.transientFailures.set(tracked.id, failures);
    if (failures >= MAX_CONSECUTIVE_TRANSIENT_FAILURES) {
      return {
        ...tracked,
        status: 'unreachable',
        error: JOB_UNREACHABLE_MESSAGE,
      };
    }
    return tracked;
  }

  private isGone(err: InterceptedError): boolean {
    return typeof err !== 'string' && err?.data?.status === GONE_MARKER;
  }

  private messageOf(err: InterceptedError): string | undefined {
    return typeof err === 'string' ? err : err?.error;
  }

  /**
   * The status before the first poll answers. `queued` is the initial state of the backend,
   * and the first poll is immediate. Thus a rehydrated job shows as in progress. A new invented
   * status would force a special case in the header indicator.
   */
  private asPendingJob(stored: StoredJob): TrackedJob {
    return {
      id: stored.id,
      kind: stored.kind,
      label: stored.label,
      context: stored.context,
      seen: stored.seen,
      status: 'queued',
    };
  }

  /**
   * The id, kind, label, context and seen flag persist. The server owns the status and the
   * result for 7 days, so a reload polls again and gets the terminal result. `seen` is
   * client-only, because the server has no such concept. It must survive a reload, like the
   * removal that `dismiss()` does. There is no per-user key. The endpoint is company-scoped, so
   * an old entry from another user of this browser answers 404 and shows as `gone`.
   */
  private readRegistry(): StoredJob[] {
    try {
      const stored = this.storageService.getObject(TRACKED_JOBS_STORAGE_KEY);
      return Array.isArray(stored)
        ? stored.filter(entry => entry?.id && entry?.kind)
        : [];
    } catch {
      // Hand-edited or truncated localStorage must not break app bootstrap.
      return [];
    }
  }

  private writeRegistry(jobs: TrackedJob[]): void {
    if (!jobs.length) {
      this.storageService.remove(TRACKED_JOBS_STORAGE_KEY);
      return;
    }
    this.storageService.setObject(
      TRACKED_JOBS_STORAGE_KEY,
      jobs.map(({ id, kind, label, context, seen }) => ({
        id,
        kind,
        label,
        context,
        seen,
      })),
    );
  }
}
